import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { usePortalEnabled, usePortalReady } from './hooks/usePortalEnabled';
import Layout from './components/layout/Layout';
import { supabase } from './lib/supabase';

const HomePage = lazy(() => import('./pages/HomePage'));
const PreviousHomePage = lazy(() => import('./pages/PreviousHomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const ResourceDetailPage = lazy(() => import('./pages/ResourceDetailPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BrandAssetsPage = lazy(() => import('./pages/BrandAssetsPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const TermsV1_3Page = lazy(() => import('./pages/terms/TermsV1_3Page'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelledPage = lazy(() => import('./pages/CheckoutCancelledPage'));
const ClientLoginPage = lazy(() => import('./pages/dashboard/ClientLoginPage'));
const ClientDashboardPage = lazy(() => import('./pages/dashboard/ClientDashboardPage'));
const ResetPasswordPage = lazy(() => import('./pages/dashboard/ResetPasswordPage'));
const AdminPanelPage = lazy(() => import('./pages/admin/AdminPanelPage'));
const SubmitCredentialsPage = lazy(() => import('./pages/SubmitCredentialsPage'));
const ConfirmAccessPage = lazy(() => import('./pages/ConfirmAccessPage'));
const UploadLargeFilesPage = lazy(() => import('./pages/UploadLargeFilesPage'));

const SHORT_SLUG_MAP: Record<string, string> = {
  cwb: 'complete-website-build',
  fcwb: 'fully-custom-website-build',
  sab: 'strategic-architecture-build',
  tshp: 'two-strategic-homepage-prototypes',
  wda: 'website-design-audit',
  wcwso: 'website-content-writing',
  src: 'seo-reality-check',
};

function TermsVersionRouter() {
  const { version } = useParams<{ version: string }>();
  if (version === 'v1-3') return <TermsV1_3Page />;
  return <Navigate to="/404" replace />;
}

function ServiceShortSlugRedirect() {
  const { shortSlug } = useParams<{ shortSlug: string }>();
  const [searchParams] = useSearchParams();
  const fullSlug = shortSlug ? SHORT_SLUG_MAP[shortSlug] : undefined;
  if (!fullSlug) return <Navigate to="/404" replace />;
  const params = new URLSearchParams(searchParams);
  params.set('openIntake', 'true');
  return <Navigate to={`/services/${fullSlug}?${params.toString()}`} replace />;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-3 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
    </div>
  );
}

// Wraps portal-only pages: waits for the DB check, then redirects to /404 when portal is disabled.
// Authenticated admins bypass the portal-enabled check so they can always preview the client workspace.
function PortalGuard({ children }: { children: React.ReactNode }) {
  const portalEnabled = usePortalEnabled();
  const portalReady = usePortalReady();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { if (mounted) setIsAdmin(false); return; }
      const { data } = await supabase
        .from('admin_users')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('is_active', true)
        .maybeSingle();
      if (mounted) setIsAdmin(!!data);
    })();
    return () => { mounted = false; };
  }, []);

  // Wait until both the portal setting and admin check are resolved
  if (!portalReady || isAdmin === null) return null;
  // Admins always get through regardless of portal_enabled
  if (isAdmin) return <>{children}</>;
  if (!portalEnabled) return <Navigate to="/404" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/client" element={<PortalGuard><ClientLoginPage /></PortalGuard>} />
          <Route path="/client/reset-password" element={<PortalGuard><ResetPasswordPage /></PortalGuard>} />
          <Route path="/client/:clientSlug/workspace/*" element={<PortalGuard><ClientDashboardPage /></PortalGuard>} />
          <Route path="/ops-x7k9m2v8q3w1/*" element={<AdminPanelPage />} />
          <Route path="/submit-credentials" element={<SubmitCredentialsPage />} />
          <Route path="/confirm-access" element={<ConfirmAccessPage />} />
          <Route path="/upload-files" element={<UploadLargeFilesPage />} />
          <Route element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="homepage-previous-version" element={<PreviousHomePage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="services/:slug" element={<ServiceDetailPage />} />
            <Route path="services/:shortSlug/intake" element={<ServiceShortSlugRedirect />} />
            <Route path="services/:shortSlug/intake-payment" element={<ServiceShortSlugRedirect />} />
            <Route path="services/:shortSlug/acknowledgment-payment" element={<ServiceShortSlugRedirect />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="resources/:slug" element={<ResourceDetailPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<ArticlePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="brand-assets" element={<BrandAssetsPage />} />
            <Route path="terms" element={<TermsPage />} />
            <Route path="terms/:version" element={<TermsVersionRouter />} />
            <Route path="checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="checkout/cancelled" element={<CheckoutCancelledPage />} />
            <Route path="404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
